import { COLOR } from 'app-v2/design-tokens'
import { Status } from 'app/assets/icons'
import { CaptionText } from 'app/common/typography'
import React from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import type { StatusOpening } from 'shared/store/store-locator'
import {
    calculateMinutesFromNowToTime,
    featureCodes,
    restaurantStatus
} from 'shared/utils'
import styled from 'styled-components'
import { string } from 'yup'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'

const { red, orange } = oldColors

type Props = {
    status: StatusOpening,
    driveStatus: StatusOpening,
    color?: string,
    openingHour: string,
    closingHour: string,
    clickAndCollect?: boolean,
    intl: IntlShape,
    id?: string
}

const OpeningStatusComponent = ({
    status,
    driveStatus,
    color: textColor = COLOR.PRIMARY,
    openingHour,
    closingHour,
    clickAndCollect,
    intl,
    id
}: Props) => {
    let openingStatus

    switch (status) {
        case restaurantStatus.SOON:
            openingStatus = {
                key: 'storelocator.restaurant.soon',
                color: orange,
                time: null
            }
            break
        case restaurantStatus.OPENED:
            openingStatus = {
                key: clickAndCollect
                    ? 'restaurant.cac.opened'
                    : 'storelocator.restaurant.opened',
                color: colors.crunchyGreen,
                time: closingHour
            }
            break
        case restaurantStatus.IMMINENT_OPENING:
            openingStatus = {
                key: clickAndCollect
                    ? 'restaurant.cac.imminent.opening'
                    : 'restaurant.imminent.opening',
                color: orange,
                time: calculateMinutesFromNowToTime(openingHour)
            }
            break
        case restaurantStatus.IMMINENT_CLOSING:
            openingStatus = {
                key: clickAndCollect
                    ? 'restaurant.cac.imminent.closing'
                    : 'restaurant.imminent.closing',
                color: orange,
                time: calculateMinutesFromNowToTime(closingHour)
            }
            break
        case restaurantStatus.CLOSED:
        default:
            openingStatus = {
                key: clickAndCollect
                    ? 'restaurant.cac.closed'
                    : 'restaurant.closed',
                color: red,
                time: null
            }
    }

    if (!status) {
        openingStatus = {
            key: 'restaurant.no.status',
            color: red,
            time: null
        }
    }

    if (
        (status !== restaurantStatus.OPENED || !status) &&
        (driveStatus === restaurantStatus.OPENED ||
            driveStatus === restaurantStatus.IMMINENT_CLOSING)
    ) {
        openingStatus = {
            key: 'storelocator.restaurant.opened.only-drive',
            color: colors.crunchyGreen,
            time: null
        }
    }

    const {
        key,
        color,
        time = intl.formatMessage({ id: 'restaurant.no.time' })
    } = openingStatus

    return (
        <OpeningStatusContainer>
            <Status color={color} />
            <FormattedMessage id={key} values={{ time }}>
                {(text: string) => (
                    <CaptionText color={textColor}>{text}</CaptionText>
                )}
            </FormattedMessage>
        </OpeningStatusContainer>
    )
}

export const OpeningStatusContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    ${CaptionText} {
        margin-left: 5px;
    }
`

export const OpeningStatus = injectIntl(OpeningStatusComponent)
